import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const MaintenanceRequestModal = ({ isOpen, onClose, equipment, onSubmit }) => {
  const [formData, setFormData] = useState({
    requestType: '',
    priority: 'medium',
    description: '',
    symptoms: '',
    requestedDate: new Date()?.toISOString()?.split('T')?.[0],
    estimatedDuration: '',
    requiredParts: '',
    specialInstructions: '',
    requestedBy: 'Dr. Sarah Chen',
    department: equipment?.department || '',
    contactInfo: 'sarah.chen@ifet.gr'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requestTypes = [
    'Preventive Maintenance',
    'Corrective Maintenance',
    'Emergency Repair',
    'Calibration Service',
    'Software Update',
    'Hardware Upgrade',
    'Cleaning Service',
    'Performance Verification'
  ];

  const priorityLevels = [
    { value: 'low', label: 'Low', color: 'text-success', description: 'Non-critical, can wait' },
    { value: 'medium', label: 'Medium', color: 'text-warning', description: 'Standard priority' },
    { value: 'high', label: 'High', color: 'text-error', description: 'Urgent, affects operations' },
    { value: 'critical', label: 'Critical', color: 'text-error', description: 'Emergency, immediate attention' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);

    try {
      const maintenanceRequest = {
        ...formData,
        equipmentId: equipment?.id,
        deviceId: equipment?.deviceId,
        equipmentName: equipment?.name,
        requestId: `MR-${Date.now()}`,
        status: 'pending',
        submissionDate: new Date()?.toISOString(),
        blockchainHash: `0x${Math.random()?.toString(16)?.substr(2, 64)}` // Mock blockchain hash
      };

      await onSubmit(maintenanceRequest);
      onClose();
    } catch (error) {
      console.error('Error submitting maintenance request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPriorityConfig = (priority) => {
    return priorityLevels?.find(p => p?.value === priority) || priorityLevels?.[1];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-popover border border-border rounded-lg shadow-modal overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Wrench" size={20} className="text-warning" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-popover-foreground">Maintenance Request</h2>
              <p className="text-sm text-muted-foreground">
                {equipment?.deviceId} - {equipment?.name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground transition-colors duration-150"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Equipment Information */}
            <div className="bg-muted/20 rounded-lg p-4">
              <h3 className="font-medium text-foreground mb-3">Equipment Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Device ID</p>
                  <p className="font-medium text-foreground">{equipment?.deviceId}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Model</p>
                  <p className="font-medium text-foreground">{equipment?.model}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Serial Number</p>
                  <p className="font-medium text-foreground">{equipment?.serialNumber}</p>
                </div>
              </div>
            </div>

            {/* Request Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-popover-foreground mb-2">
                  Request Type *
                </label>
                <select
                  value={formData?.requestType}
                  onChange={(e) => handleInputChange('requestType', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground"
                  required
                >
                  <option value="">Select request type</option>
                  {requestTypes?.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-popover-foreground mb-2">
                  Priority Level *
                </label>
                <select
                  value={formData?.priority}
                  onChange={(e) => handleInputChange('priority', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground"
                  required
                >
                  {priorityLevels?.map(priority => (
                    <option key={priority?.value} value={priority?.value}>
                      {priority?.label} - {priority?.description}
                    </option>
                  ))}
                </select>
                <div className="mt-2 flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${
                    formData?.priority === 'critical' || formData?.priority === 'high' ? 'bg-error' :
                    formData?.priority === 'medium' ? 'bg-warning' : 'bg-success'
                  }`}></div>
                  <span className={`text-xs font-medium ${getPriorityConfig(formData?.priority)?.color}`}>
                    {getPriorityConfig(formData?.priority)?.label} Priority
                  </span>
                </div>
              </div>
            </div>

            {/* Description and Symptoms */}
            <div>
              <label className="block text-sm font-medium text-popover-foreground mb-2">
                Problem Description *
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground h-24 resize-none"
                placeholder="Describe the issue or maintenance requirement in detail"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-popover-foreground mb-2">
                Symptoms Observed
              </label>
              <textarea
                value={formData?.symptoms}
                onChange={(e) => handleInputChange('symptoms', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground h-20 resize-none"
                placeholder="List any error messages, unusual sounds, performance issues, etc."
              />
            </div>

            {/* Scheduling and Requirements */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Requested Service Date"
                type="date"
                value={formData?.requestedDate}
                onChange={(e) => handleInputChange('requestedDate', e?.target?.value)}
                required
              />
              
              <Input
                label="Estimated Duration"
                type="text"
                value={formData?.estimatedDuration}
                onChange={(e) => handleInputChange('estimatedDuration', e?.target?.value)}
                placeholder="e.g., 2 hours, Half day, Full day"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-popover-foreground mb-2">
                Required Parts/Materials
              </label>
              <textarea
                value={formData?.requiredParts}
                onChange={(e) => handleInputChange('requiredParts', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground h-20 resize-none"
                placeholder="List any known parts, consumables, or materials needed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-popover-foreground mb-2">
                Special Instructions
              </label>
              <textarea
                value={formData?.specialInstructions}
                onChange={(e) => handleInputChange('specialInstructions', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground h-20 resize-none"
                placeholder="Any special requirements, safety considerations, or access restrictions"
              />
            </div>

            {/* Contact Information */}
            <div className="bg-muted/20 rounded-lg p-4">
              <h3 className="font-medium text-foreground mb-3">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Requested By"
                  type="text"
                  value={formData?.requestedBy}
                  onChange={(e) => handleInputChange('requestedBy', e?.target?.value)}
                  required
                />
                
                <Input
                  label="Contact Email"
                  type="email"
                  value={formData?.contactInfo}
                  onChange={(e) => handleInputChange('contactInfo', e?.target?.value)}
                  required
                />
              </div>
            </div>

            {/* Urgency Notice */}
            {(formData?.priority === 'high' || formData?.priority === 'critical') && (
              <div className="bg-error/5 border border-error/20 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <Icon name="AlertTriangle" size={20} className="text-error" />
                  <div>
                    <p className="text-sm font-medium text-foreground">High Priority Request</p>
                    <p className="text-xs text-muted-foreground">
                      This request will be escalated for immediate attention. Emergency contact will be notified.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Blockchain Notice */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <Icon name="Shield" size={20} className="text-primary" />
                <div>
                  <p className="text-sm font-medium text-foreground">Blockchain Logging</p>
                  <p className="text-xs text-muted-foreground">
                    This maintenance request will be recorded on the blockchain for audit trail and compliance tracking.
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-4 p-6 border-t border-border bg-muted/20">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            iconName="Send"
            iconSize={16}
          >
            Submit Request
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceRequestModal;